import { useState, useMemo, useEffect, useRef } from 'react';
import { MagnifyingGlass, X, User } from '@phosphor-icons/react';

/**
 * Searchable volunteer picker with inline filter.
 *
 * Renders a button that, when clicked, opens a popover with a search input
 * and the filtered list of volunteers. Filters by name, V-code (study
 * assignment code) and DNI as the user types.
 *
 * Props:
 *   options: array of volunteer-like objects. Each must have a stable id
 *            via getId(o). Optional fields used by the default formatter:
 *            name, dni, study_volunteer_code, status, num_voluntario.
 *   value:   currently selected id (volunteer_id) or '' for empty.
 *   onChange: (id, option) => void
 *   getId:   (o) => string. Defaults to o.volunteer_id.
 *   getLabel: (o) => string. Display label inside the trigger and list.
 *   getSearchText: (o) => string. Concatenated text used for matching.
 *   placeholder, emptyHint, disabled, dataTestid
 */
export default function VolunteerComboSearch({
  options,
  value,
  onChange,
  getId = (o) => o.volunteer_id,
  getLabel,
  getSearchText,
  placeholder = 'Buscar voluntario...',
  emptyHint = 'No hay coincidencias.',
  disabled = false,
  dataTestid = 'vol-combo',
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);
  const wrapRef = useRef(null);

  const _label = getLabel || ((o) => o?.volunteer_name || o?.name || getId(o));
  const _search = getSearchText || ((o) => `${o.volunteer_name || ''} ${o.name || ''} ${o.study_volunteer_code || ''} ${o.volunteer_dni || ''} ${o.dni || ''} ${(o.personal_data?.num_voluntario) || ''}`.toLowerCase());

  const selected = useMemo(() => options.find(o => getId(o) === value) || null, [options, value, getId]);

  const norm = (s) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const filtered = useMemo(() => {
    if (!query.trim()) return options.slice(0, 50);
    const q = norm(query);
    return options.filter(o => norm(_search(o)).includes(q)).slice(0, 100);
  }, [options, query, _search]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // Focus input when opened
  useEffect(() => {
    if (open && inputRef.current) {
      const t = setTimeout(() => inputRef.current?.focus(), 30);
      return () => clearTimeout(t);
    }
  }, [open]);

  const handleSelect = (o) => {
    onChange(getId(o), o);
    setOpen(false);
    setQuery('');
  };

  return (
    <div className="relative" ref={wrapRef} data-testid={dataTestid}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(o => !o)}
        className={`w-full text-left flex items-center gap-2 px-3 py-2 text-sm border border-stone-200 bg-white rounded-sm hover:border-stone-300 focus:outline-none focus:ring-2 focus:ring-[#1f3e80]/20 ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
        data-testid={`${dataTestid}-trigger`}
      >
        <User size={14} className="text-stone-400 shrink-0" />
        <span className="flex-1 truncate">
          {selected ? _label(selected) : <span className="text-stone-400">{placeholder}</span>}
        </span>
        {selected && !disabled && (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); onChange('', null); }}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); onChange('', null); } }}
            className="text-stone-400 hover:text-stone-600 cursor-pointer"
            data-testid={`${dataTestid}-clear`}
          >
            <X size={12} />
          </span>
        )}
      </button>

      {open && !disabled && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-stone-200 rounded-sm shadow-lg max-h-72 flex flex-col">
          <div className="flex items-center gap-2 px-2 py-1.5 border-b border-stone-100">
            <MagnifyingGlass size={14} className="text-stone-400" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder={placeholder}
              className="flex-1 text-sm bg-transparent outline-none placeholder:text-stone-400"
              data-testid={`${dataTestid}-input`}
            />
            {query && (
              <button type="button" onClick={() => setQuery('')} className="text-stone-400 hover:text-stone-700">
                <X size={12} />
              </button>
            )}
          </div>
          <div className="overflow-y-auto flex-1 py-1">
            {filtered.length === 0 ? (
              <p className="px-3 py-3 text-xs text-stone-400 italic">{emptyHint}</p>
            ) : (
              filtered.map(o => {
                const id = getId(o);
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => handleSelect(o)}
                    className={`w-full text-left px-3 py-1.5 text-sm hover:bg-stone-50 flex items-center gap-2 ${id === value ? 'bg-blue-50 text-[#1f3e80] font-medium' : 'text-stone-700'}`}
                    data-testid={`${dataTestid}-opt-${id}`}
                  >
                    <span className="flex-1 truncate">{_label(o)}</span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
